import axios from "axios";
import { useEffect, useState, useMemo, useRef } from "react";
import Navbar from "../components/Navbar";
import EarnixLogo from "../components/EarnixLogo";
import useBinancePrices from "../hooks/useBinancePrices";
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";

import API from "../config.js";
const G    = "#FFB800";
const G2   = "#FFD700";
const GD   = "#C8860A";
const GRAD = `linear-gradient(135deg, ${G2}, ${G}, ${GD})`;

const DASH_SYMS = [
  {pair:"BTC/USDT",b:"BTCUSDT"},{pair:"ETH/USDT",b:"ETHUSDT"},{pair:"BNB/USDT",b:"BNBUSDT"},
  {pair:"SOL/USDT",b:"SOLUSDT"},{pair:"XRP/USDT",b:"XRPUSDT"},{pair:"DOGE/USDT",b:"DOGEUSDT"},
  {pair:"ADA/USDT",b:"ADAUSDT"},{pair:"TRX/USDT",b:"TRXUSDT"},{pair:"AVAX/USDT",b:"AVAXUSDT"},
  {pair:"DOT/USDT",b:"DOTUSDT"},{pair:"LINK/USDT",b:"LINKUSDT"},{pair:"POL/USDT",b:"POLUSDT"},
];

function DashTicker() {
  const trackRef = useRef(null);
  const rafRef   = useRef(null);
  const posRef   = useRef(0);
  const halfRef  = useRef(0);

  useEffect(() => {
    const updateDOM = (bSym, price, pct) => {
      if (!trackRef.current) return;
      const col  = pct >= 0 ? "#00e87a" : "#ff6b6b";
      const pStr = price > 0
        ? "$" + price.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        : "—";
      const qStr = `${pct >= 0 ? "▲" : "▼"}${Math.abs(pct).toFixed(2)}%`;
      trackRef.current.querySelectorAll(`[data-b="${bSym}"] [data-p]`).forEach(el => {
        el.textContent = pStr; el.style.color = col;
      });
      trackRef.current.querySelectorAll(`[data-b="${bSym}"] [data-q]`).forEach(el => {
        el.textContent = qStr; el.style.color = col;
      });
    };

    const symsStr = DASH_SYMS.map(s => `"${s.b}"`).join(",");
    fetch(`https://api.binance.com/api/v3/ticker/24hr?symbols=[${symsStr}]`)
      .then(r => r.json())
      .then(json => { if (Array.isArray(json)) json.forEach(d => updateDOM(d.symbol, parseFloat(d.lastPrice)||0, parseFloat(d.priceChangePercent)||0)); })
      .catch(() => {});

    const streams = DASH_SYMS.map(s => `${s.b.toLowerCase()}@ticker`).join("/");
    let ws;
    try {
      ws = new WebSocket(`wss://stream.binance.com:9443/stream?streams=${streams}`);
      ws.onmessage = (e) => {
        try { const d = JSON.parse(e.data).data; if (d?.s) updateDOM(d.s, parseFloat(d.c)||0, parseFloat(d.P)||0); } catch {}
      };
    } catch {}
    return () => { try { ws?.close(); } catch {} };
  }, []);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const r0 = requestAnimationFrame(() => {
      halfRef.current = track.scrollWidth / 2;
      const step = () => {
        posRef.current += 0.5;
        if (posRef.current >= halfRef.current) posRef.current = 0;
        track.style.transform = `translate3d(${-posRef.current}px,0,0)`;
        rafRef.current = requestAnimationFrame(step);
      };
      rafRef.current = requestAnimationFrame(step);
    });
    return () => { cancelAnimationFrame(r0); cancelAnimationFrame(rafRef.current); };
  }, []);

  const allItems = [...DASH_SYMS, ...DASH_SYMS];
  return (
    <div style={s.aiStrip}>
      <span style={s.aiLabel}>🤖 LIVE</span>
      <div style={s.tickerOuter}>
        <div ref={trackRef} style={{ display:"flex", alignItems:"center", whiteSpace:"nowrap", willChange:"transform" }}>
          {allItems.map((c, i) => (
            <span key={i} data-b={c.b} style={s.aiItem}>
              <span style={s.aiPair}>{c.pair}</span>
              <span data-p style={{ color:"#00e87a", fontWeight:700, fontFamily:"'Figtree',sans-serif", fontSize:12, display:"inline-block", minWidth:80, textAlign:"right" }}>—</span>
              <span data-q style={{ color:"#00e87a", fontSize:11, fontWeight:700, display:"inline-block", minWidth:56 }}>▲0.00%</span>
              <span style={s.tickerDivider}>|</span>
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [data,         setData]         = useState(null);
  const [notices,      setNotices]      = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [copied,       setCopied]       = useState(false);
  const [earningsData, setEarningsData] = useState([]);
  const aiPrices = useBinancePrices();
  const [userCount, setUserCount] = useState(Math.floor(Math.random() * 500) + 2000);

  const token   = localStorage.getItem("token");
  const headers = { Authorization: "Bearer " + token };

  useEffect(() => {
    if (!token) { window.location.href = "/"; return; }
    Promise.all([
      axios.get(`${API}/api/dashboard`,         { headers }),
      axios.get(`${API}/api/notices`,           { headers }).catch(() => ({ data: [] })),
      axios.get(`${API}/api/dashboard/earnings`,{ headers }).catch(() => ({ data: [] })),
    ]).then(([d, n, e]) => {
      setData(d.data);
      setNotices(Array.isArray(n.data) ? n.data : []);
      setEarningsData(Array.isArray(e.data) ? e.data : []);
    }).catch((err) => {
      if (err.response && err.response.status === 401) {
        localStorage.removeItem("token");
        window.location.href = "/";
      }
    }).finally(() => setLoading(false));
  }, []);


  const refLink = `${window.location.origin}/login?ref=${data?.referralCode || ""}`;
  const copyRef = () => {
    navigator.clipboard.writeText(refLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) return <LoadingScreen />;

  const bal  = Number(data?.balance || 0);
  const inv  = data?.investment || null;
  const PLAN_COLORS = { Starter:"#00C2FF", Silver:"#C0C8D8", Gold:G2, Platinum:"#B8A0FF", Elite:"#FF9900" };
  const invColor = inv ? (PLAN_COLORS[inv.planName] || G) : G;

  const stats = [
    { label: "Wallet Balance",  value: fmtU(bal),                  color: G,        icon: "◈", sub: "Available USDT" },
    { label: "Total Deposit",   value: fmtU(data?.totalDeposit),   color: "#00e87a", icon: "↑", sub: "All time" },
    { label: "Total Withdraw",  value: fmtU(data?.totalWithdraw),  color: "#ff6b6b", icon: "↓", sub: "All time" },
    { label: "Total Earnings",  value: fmtU(data?.totalEarning),   color: G2,        icon: "★", sub: "Referral + Interest" },
  ];

  return (
    <div style={s.page}>
      <Navbar />

      {/* AI ticker strip */}
      <DashTicker />

      <div style={s.container} className="page-container">

        {/* ── HEADER ── */}
        <div style={s.header} className="page-header">
          <div style={s.headerLeft}>
            <div style={s.avatarRing}>
              <div style={s.avatar}>
                {(data?.name || "U")[0].toUpperCase()}
              </div>
            </div>
            <div>
              <div style={s.greeting}>Welcome back,</div>
              <h1 style={s.userName}>{data?.name || "Trader"}</h1>
              <div style={s.userEmail}>{data?.email}</div>
            </div>
          </div>
          <div style={s.headerRight}>
            <div style={s.planBadge}>
              <span style={s.planDot} />{(inv?.planName || data?.plan || "Starter").toUpperCase()} PLAN
            </div>
            <div style={s.joinDate}>
              Member since {data?.joinDate ? new Date(data.joinDate).toLocaleDateString("en-IN", { month: "short", year: "numeric" }) : "—"}
            </div>
          </div>
        </div>

        {/* ── MAIN BALANCE HERO ── */}
        <div style={s.balanceHero} className="balance-hero">
          <div style={s.bhGlow} />
          <div style={s.bhLeft}>
            <div style={s.bhLabel}>Total Balance</div>
            <div style={s.bhValue}>{fmtU(bal)}</div>
            <div style={s.bhSub}>≈ ${bal.toFixed(2)} USD</div>
          </div>
          <div style={s.bhRight}>
            <a href="/wallet" style={s.bhBtnPrimary}>↑ Deposit</a>
            <a href="/wallet" style={s.bhBtnSecondary}>↓ Withdraw</a>
          </div>
          <div style={s.bhBgLogo} className="bh-bg-logo"><EarnixLogo size={110} glow={false} /></div>
        </div>

        {/* ── STATS GRID ── */}
        <div style={s.statsGrid}>
          {stats.map((st, i) => (
            <div key={i} style={s.statCard}>
              <div style={s.scGlowLine} />
              <div style={s.scTop}>
                <div style={{ ...s.scIcon, color: st.color, background: st.color + "12", borderColor: st.color + "30" }}>
                  {st.icon}
                </div>
                <span style={s.scSub}>{st.sub}</span>
              </div>
              <div style={{ ...s.scValue, color: st.color }}>{st.value}</div>
              <div style={s.scLabel}>{st.label}</div>
            </div>
          ))}
        </div>

        {/* ── INVESTMENT CARD ── */}
        {inv ? (
          <div style={{...s.panel, borderColor: invColor+"30", marginBottom:20}}>
            <div style={s.panelHead}>
              <span style={s.panelIcon}>💼</span>
              <span style={s.panelTitle}>Active Investment</span>
              <span style={{...s.panelBadge, color: invColor, background: invColor+"14", borderColor: invColor+"30"}}>{inv.planName} Plan</span>
            </div>
            <div className="inv-stats-grid" style={{display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:0, borderTop:"1px solid rgba(255,184,0,0.07)", marginTop:12}}>
              {[
                {lbl:"Invested",      val: fmtU(inv.amount),                               color: invColor},
                {lbl:"Daily Rate",    val: `${(inv.dailyRate*100).toFixed(2)}%/day`,        color: invColor},
                {lbl:"Daily Income",  val: `+${fmtU(inv.amount*inv.dailyRate)} USDT`,       color:"#00e87a"},
                {lbl:"Total Earned",  val: fmtU(inv.totalEarned),                          color: G2},
              ].map((it,i)=>(
                <div key={i} style={{padding:"14px 16px", textAlign:"center", borderRight: i<3?"1px solid rgba(255,184,0,0.07)":"none"}}>
                  <div style={{fontFamily:"'-apple-system','BlinkMacSystemFont','Figtree',sans-serif", fontSize:10, color:"rgba(160,180,220,0.4)", textTransform:"uppercase", letterSpacing:1, marginBottom:6}}>{it.lbl}</div>
                  <div style={{fontFamily:"'Figtree',sans-serif", fontWeight:700, fontSize:14, color:it.color}}>{it.val}</div>
                </div>
              ))}
            </div>
            <div style={{padding:"12px 0 4px", textAlign:"center"}}>
              <a href="/invest" style={{fontFamily:"'-apple-system','BlinkMacSystemFont','Figtree',sans-serif", fontSize:12, color:invColor, textDecoration:"none", letterSpacing:0.5}}>
                Manage Investment →
              </a>
            </div>
          </div>
        ) : (
          <div style={{...s.panel, marginBottom:20, borderColor:"rgba(255,184,0,0.1)"}}>
            <div style={{display:"flex", alignItems:"center", justifyContent:"space-between", padding:"4px 0 12px"}}>
              <div>
                <div style={{fontFamily:"'Figtree',sans-serif", fontWeight:700, fontSize:14, color:G, marginBottom:6}}>Start Investing</div>
                <div style={{fontFamily:"'-apple-system','BlinkMacSystemFont','Figtree',sans-serif", fontSize:13, color:"rgba(160,180,220,0.5)"}}>Earn up to 1.7% daily on your invested USDT</div>
              </div>
              <a href="/invest" style={{fontFamily:"'Figtree',sans-serif", fontWeight:700, fontSize:11, color:"#060D1A", background:GRAD, borderRadius:8, padding:"10px 18px", textDecoration:"none", letterSpacing:1, whiteSpace:"nowrap"}}>
                Invest Now →
              </a>
            </div>
          </div>
        )}

        {/* ── CHARTS ── */}
        <ChartsSection data={data} inv={inv} earningsData={earningsData} />

        {/* ── BOTTOM GRID ── */}
        <div style={s.bottomGrid} className="bottom-grid">

          {/* Referral Card */}
          <div style={s.panel}>
            <div style={s.panelHead}>
              <span style={s.panelIcon}>⬡</span>
              <span style={s.panelTitle}>Referral Program</span>
              <span style={s.panelBadge}>10% Instant</span>
            </div>
            <p style={s.refDesc}>
              Share your unique code — earn <b style={{ color: G }}>10% instantly</b> on every deposit your referrals make, plus <b style={{ color: G2 }}>10-level passive income</b>.
            </p>
            <div style={s.refBox}>
              <span style={{...s.refCode, fontSize:11, letterSpacing:0.3, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", flex:1}}>
                {refLink}
              </span>
              <button style={{ ...s.copyBtn, background: copied ? "#00e87a" : GRAD, flexShrink:0 }}
                onClick={copyRef}>
                {copied ? "✓ Copied!" : "Copy Link"}
              </button>
            </div>
            <div style={s.levelGrid}>
              {[10, 5, 3, 2, 1, 1, 0.5, 0.5, 0.3, 0.3].map((pct, i) => (
                <div key={i} style={s.levelItem}>
                  <div style={s.levelNum}>L{i + 1}</div>
                  <div style={{ ...s.levelPct, color: i === 0 ? G2 : i < 3 ? G : "rgba(200,160,60,0.5)" }}>
                    {pct}%
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* AI Performance + Notices */}
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>

            {/* AI Performance */}
            <div style={s.aiCard}>
              <div style={s.panelHead}>
                <span style={s.panelIcon}>🤖</span>
                <span style={s.panelTitle}>AI Trading Performance</span>
                <span style={{ ...s.panelBadge, color: "#00e87a", borderColor: "#00e87a44", background: "rgba(0,232,122,0.08)" }}>LIVE</span>
              </div>
              <div style={s.aiPrices}>
                {aiPrices.map((p, i) => (
                  <div key={i} style={s.aiRow}>
                    <span style={s.aiRowPair}>{p.pair}</span>
                    <div style={s.aiBar}>
                      <div style={{
                        height: "100%",
                        width: `${40 + Math.random() * 50}%`,
                        background: p.up ? "rgba(0,232,122,0.3)" : "rgba(255,107,107,0.3)",
                        borderRadius: 2, transition: "width 1s",
                      }} />
                    </div>
                    <span style={{ color: p.up ? "#00e87a" : "#ff6b6b", fontFamily: "'Figtree',sans-serif", fontSize: 12, fontWeight: 700 }}>
                      {p.up ? "+" : ""}{p.pct}%
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Notices */}
            <div style={s.panel}>
              <div style={s.panelHead}>
                <span style={s.panelIcon}>◉</span>
                <span style={s.panelTitle}>Announcements</span>
              </div>
              {notices.length === 0 ? (
                <div style={s.noNotice}>No announcements at this time.</div>
              ) : (
                <div style={s.noticeList}>
                  {notices.slice(0, 4).map((n, i) => (
                    <div key={i} style={{ ...s.noticeItem, borderLeftColor: noticeColor(n.type) }}>
                      <div style={s.noticeTitle}>{n.title}</div>
                      <div style={s.noticeMsg}>{n.message}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── QUICK ACTIONS ── */}
        <div style={s.quickActions}>
          <div style={s.qaTitle}>Quick Actions</div>
          <div style={s.qaRow}>
            {[
              { label: "Deposit USDT",    icon: "↑", href: "/wallet",        style: s.qaBtnGold },
              { label: "Withdraw",        icon: "↓", href: "/wallet",        style: s.qaBtnRed  },
              { label: "Transactions",    icon: "◫", href: "/transactions",  style: s.qaBtnGhost },
              { label: "Share Referral",  icon: "⬡", href: "#",             style: s.qaBtnGhost,
                onClick: () => { navigator.clipboard.writeText(refLink); } },
            ].map((a, i) => (
              <a key={i} href={a.href} style={a.style} onClick={a.onClick}>
                <span style={{ fontSize: 18 }}>{a.icon}</span>
                <span>{a.label}</span>
              </a>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Charts Section ── */
function ChartsSection({ data, inv, earningsData }) {
  const bal         = Number(data?.balance      || 0);
  const totalDep    = Number(data?.totalDeposit || 0);
  const totalWith   = Number(data?.totalWithdraw|| 0);
  const totalEarn   = Number(data?.totalEarning || 0);
  const invested    = Number(inv?.amount        || 0);
  const totalEarned = Number(inv?.totalEarned   || 0);

  // Use real 7-day data from server; fallback to zeros if empty
  const chartData = useMemo(() => {
    if (earningsData && earningsData.length > 0) return earningsData;
    const days = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];
    return days.map(day => ({ day, interest: 0, referral: 0, deposit: 0, total: 0 }));
  }, [earningsData]);

  /* Portfolio donut */
  const portfolioData = [
    { name: "Wallet",   value: bal,         color: "#FFB800" },
    { name: "Invested", value: invested,     color: "#00C2FF" },
    { name: "Earned",   value: totalEarned,  color: "#00e87a" },
  ].filter(d => d.value > 0);
  const totalPortfolio = portfolioData.reduce((a, d) => a + d.value, 0);

  /* Deposit vs Withdrawal simplified */
  const depWithData = [
    { label: "Deposits",     value: totalDep,   color: "#00e87a" },
    { label: "Withdrawals",  value: totalWith,  color: "#ff6b6b" },
    { label: "Earnings",     value: totalEarn,  color: "#FFB800" },
  ];

  const tooltipStyle = {
    contentStyle: { background: "rgba(6,13,26,0.97)", border: "1px solid rgba(255,184,0,0.2)", borderRadius: 8, fontFamily: "'-apple-system','BlinkMacSystemFont','Figtree',sans-serif", fontSize: 12 },
    labelStyle:   { color: "#FFB800", fontWeight: 700, fontFamily: "'Figtree',sans-serif", fontSize: 11 },
    itemStyle:    { color: "#e0e8ff" },
  };

  return (
    <div style={ch.wrap} className="charts-grid">
      {/* ── Left: Earnings Area Chart ── */}
      <div style={ch.card}>
        <div style={ch.topLine} />
        <div style={ch.cardHead}>
          <div>
            <div style={ch.badge}>ANALYTICS</div>
            <div style={ch.cardTitle}>7-Day Earnings Trend</div>
          </div>
          <div style={ch.statPills}>
            <span style={{...ch.pill, color:"#FFB800", background:"rgba(255,184,0,0.08)", borderColor:"rgba(255,184,0,0.2)"}}>
              Interest
            </span>
            <span style={{...ch.pill, color:"#00C2FF", background:"rgba(0,194,255,0.08)", borderColor:"rgba(0,194,255,0.2)"}}>
              Referral
            </span>
          </div>
        </div>
        <div style={{ height: 200, paddingRight: 8, paddingLeft: 8 }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 8, right: 0, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="gInterest" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor="#FFB800" stopOpacity={0.4} />
                  <stop offset="95%" stopColor="#FFB800" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gReferral" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor="#00C2FF" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#00C2FF" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,184,0,0.06)" />
              <XAxis dataKey="day" tick={{ fill: "rgba(160,180,220,0.4)", fontSize: 10, fontFamily: "'-apple-system','BlinkMacSystemFont','Figtree',sans-serif" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: "rgba(160,180,220,0.4)", fontSize: 10, fontFamily: "'-apple-system','BlinkMacSystemFont','Figtree',sans-serif" }} axisLine={false} tickLine={false} />
              <Tooltip {...tooltipStyle} formatter={(v, name) => [`${v.toFixed(2)} USDT`, name === "interest" ? "Interest" : "Referral"]} />
              <Area type="monotone" dataKey="interest" stroke="#FFB800" strokeWidth={2} fill="url(#gInterest)" dot={false} activeDot={{ r: 4, fill: "#FFB800" }} />
              <Area type="monotone" dataKey="referral"  stroke="#00C2FF" strokeWidth={2} fill="url(#gReferral)"  dot={false} activeDot={{ r: 4, fill: "#00C2FF" }} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        {/* Summary row */}
        <div style={ch.summRow}>
          <div style={ch.summItem}>
            <span style={ch.summLbl}>Total Deposit</span>
            <span style={{...ch.summVal, color:"#00e87a"}}>{fmtU(totalDep)}</span>
          </div>
          <div style={ch.summDivider} />
          <div style={ch.summItem}>
            <span style={ch.summLbl}>Total Withdraw</span>
            <span style={{...ch.summVal, color:"#ff6b6b"}}>{fmtU(totalWith)}</span>
          </div>
          <div style={ch.summDivider} />
          <div style={ch.summItem}>
            <span style={ch.summLbl}>Total Earned</span>
            <span style={{...ch.summVal, color:"#FFD700"}}>{fmtU(totalEarn)}</span>
          </div>
        </div>
      </div>

      {/* ── Right: Portfolio Donut ── */}
      <div style={ch.card}>
        <div style={ch.topLine} />
        <div style={ch.cardHead}>
          <div>
            <div style={ch.badge}>PORTFOLIO</div>
            <div style={ch.cardTitle}>Asset Breakdown</div>
          </div>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:16, padding:"0 24px" }}>
          <div style={{ width: 180, height: 180, flexShrink: 0 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={portfolioData.length > 0 ? portfolioData : [{ name: "Empty", value: 1, color: "rgba(255,184,0,0.1)" }]}
                  cx="50%" cy="50%"
                  innerRadius={52} outerRadius={80}
                  dataKey="value" strokeWidth={0}
                >
                  {(portfolioData.length > 0 ? portfolioData : [{ color: "rgba(255,184,0,0.1)" }]).map((entry, i) => (
                    <Cell key={i} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip {...tooltipStyle} formatter={(v) => [`${v.toFixed(2)} USDT`]} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div style={{ flex: 1, display:"flex", flexDirection:"column", gap:12 }}>
            {portfolioData.length > 0 ? portfolioData.map((d, i) => (
              <div key={i} style={ch.donutRow}>
                <div style={{...ch.donutDot, background: d.color}} />
                <div style={{ flex:1 }}>
                  <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
                    <span style={ch.donutLbl}>{d.name}</span>
                    <span style={{...ch.donutPct, color: d.color}}>
                      {totalPortfolio > 0 ? ((d.value/totalPortfolio)*100).toFixed(1) : 0}%
                    </span>
                  </div>
                  <div style={ch.donutTrack}>
                    <div style={{...ch.donutFill, width: totalPortfolio > 0 ? `${(d.value/totalPortfolio)*100}%` : "0%", background: d.color}} />
                  </div>
                  <div style={{...ch.donutAmt, color: d.color}}>{fmtU(d.value)}</div>
                </div>
              </div>
            )) : (
              <div style={{ fontFamily:"'-apple-system','BlinkMacSystemFont','Figtree',sans-serif", fontSize:13, color:"rgba(160,180,220,0.35)", textAlign:"center" }}>
                Make a deposit to see your portfolio breakdown.
              </div>
            )}
          </div>
        </div>
        {/* Overview bar chart */}
        <div style={{ height: 100, marginTop: 16, paddingLeft: 8, paddingRight: 8 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={depWithData} margin={{ top: 4, right: 0, left: -20, bottom: 0 }} barSize={24}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,184,0,0.05)" />
              <XAxis dataKey="label" tick={{ fill: "rgba(160,180,220,0.4)", fontSize: 10, fontFamily:"'-apple-system','BlinkMacSystemFont','Figtree',sans-serif" }} axisLine={false} tickLine={false} />
              <YAxis hide />
              <Tooltip {...tooltipStyle} formatter={(v) => [`${v.toFixed(2)} USDT`]} />
              <Bar dataKey="value" radius={[4,4,0,0]}>
                {depWithData.map((d, i) => <Cell key={i} fill={d.color} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

const ch = {
  wrap: { display:"grid", gridTemplateColumns:"1fr 1fr", gap:16, marginBottom:20 },
  card: {
    background:"rgba(8,18,40,0.95)",
    border:"1px solid rgba(255,184,0,0.1)",
    borderRadius:16, overflow:"hidden",
    boxShadow:"0 8px 40px rgba(0,0,0,0.3)",
    padding:"0 0 0 0",
  },
  topLine: { height:2, background:"linear-gradient(90deg,transparent,#FFD700,#FFB800,#C8860A,transparent)" },
  cardHead: { display:"flex", justifyContent:"space-between", alignItems:"flex-start", padding:"20px 24px 16px" },
  badge: {
    display:"inline-block",
    fontFamily:"'-apple-system','BlinkMacSystemFont','Figtree',sans-serif", fontSize:9, fontWeight:700,
    color:"#FFB800", background:"rgba(255,184,0,0.1)",
    border:"1px solid rgba(255,184,0,0.25)",
    borderRadius:20, padding:"2px 8px", letterSpacing:1.5, marginBottom:6,
  },
  cardTitle: { fontFamily:"'Figtree',sans-serif", fontWeight:700, fontSize:14, color:"#e0e8ff", letterSpacing:0.5 },
  statPills: { display:"flex", gap:6 },
  pill: {
    fontFamily:"'-apple-system','BlinkMacSystemFont','Figtree',sans-serif", fontSize:10, fontWeight:600,
    border:"1px solid", borderRadius:20, padding:"3px 10px",
  },
  summRow: {
    display:"flex", alignItems:"center",
    borderTop:"1px solid rgba(255,184,0,0.07)",
    margin:"16px 0 0",
  },
  summItem: { flex:1, padding:"14px 16px", textAlign:"center" },
  summDivider: { width:1, height:36, background:"rgba(255,184,0,0.07)" },
  summLbl: { display:"block", fontFamily:"'-apple-system','BlinkMacSystemFont','Figtree',sans-serif", fontSize:10, color:"rgba(160,180,220,0.4)", textTransform:"uppercase", letterSpacing:1, marginBottom:4 },
  summVal: { fontFamily:"'Figtree',sans-serif", fontWeight:700, fontSize:12 },
  donutRow: { display:"flex", alignItems:"flex-start", gap:10 },
  donutDot: { width:8, height:8, borderRadius:"50%", flexShrink:0, marginTop:4 },
  donutLbl: { fontFamily:"'-apple-system','BlinkMacSystemFont','Figtree',sans-serif", fontSize:12, color:"rgba(160,180,220,0.6)" },
  donutPct: { fontFamily:"'Figtree',sans-serif", fontSize:11, fontWeight:700 },
  donutTrack: { height:4, borderRadius:99, background:"rgba(255,255,255,0.05)", overflow:"hidden" },
  donutFill: { height:"100%", borderRadius:99, transition:"width 1s ease" },
  donutAmt: { fontFamily:"'Figtree',sans-serif", fontSize:11, fontWeight:700, marginTop:3 },
};

function LoadingScreen() {
  return (
    <div style={{ minHeight: "100vh", background: "#060D1A",
      display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ textAlign: "center" }}>
        <div style={{ animation: "logoPulse 1.5s ease-in-out infinite", display:"inline-block", marginBottom: 20 }}>
          <EarnixLogo size={80} glow />
        </div>
        <div style={{ fontFamily: "'Figtree',sans-serif", color: "rgba(255,184,0,0.5)", fontSize: 12, letterSpacing: 3 }}>
          LOADING...
        </div>
      </div>
    </div>
  );
}

const fmtU = v => "USDT " + (Number(v) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const noticeColor = t => ({ info: G, warning: "#ffaa00", success: "#00e87a", danger: "#ff6b6b" }[t] || G);

const s = {
  page: { minHeight: "100vh", background: "#03050a" },

  aiStrip: {
    background: "rgba(0,0,0,0.8)",
    borderBottom: "1px solid rgba(255,184,0,0.1)",
    padding: "8px 24px",
    display: "flex", alignItems: "center",
    gap: 12, overflow: "hidden",
  },
  aiLabel: {
    fontFamily: "'Figtree',sans-serif",
    fontWeight: 700, fontSize: 10,
    color: G, letterSpacing: 1.5, flexShrink: 0,
    background: "rgba(255,184,0,0.08)",
    border: "1px solid rgba(255,184,0,0.2)",
    borderRadius: 4, padding: "3px 8px",
  },
  tickerOuter: {
    flex: 1, overflow: "hidden",
  },
  tickerTrack: {
    display: "flex", alignItems: "center", gap: 0,
    animation: "smoothTicker 60s linear infinite",
    width: "max-content",
  },
  aiItem: {
    display: "flex", alignItems: "center", gap: 8,
    flexShrink: 0, padding: "0 8px",
  },
  aiPair: {
    fontFamily: "'Figtree',sans-serif",
    fontSize: 12, color: "rgba(255,184,0,0.6)",
    letterSpacing: 0.5, fontWeight: 700,
  },
  tickerDivider: {
    color: "rgba(255,184,0,0.15)", fontSize: 12, marginLeft: 8,
  },
  liveUsers: {
    fontFamily: "'-apple-system','BlinkMacSystemFont','Figtree',sans-serif",
    fontSize: 11, color: "#00e87a",
    display: "flex", alignItems: "center", gap: 5,
    flexShrink: 0, whiteSpace: "nowrap",
  },
  greenDot: {
    width: 6, height: 6, borderRadius: "50%",
    background: "#00e87a", boxShadow: "0 0 6px #00e87a",
    animation: "pulse-glow 1.5s ease-in-out infinite",
  },

  container: { maxWidth: 1200, margin: "0 auto", padding: "28px 24px" },

  header: {
    display: "flex", justifyContent: "space-between",
    alignItems: "center", marginBottom: 24,
    flexWrap: "wrap", gap: 16,
  },
  headerLeft: { display: "flex", alignItems: "center", gap: 16 },
  avatarRing: {
    width: 60, height: 60, borderRadius: "50%",
    border: `2px solid rgba(255,184,0,0.4)`,
    padding: 3, flexShrink: 0,
    background: "rgba(255,184,0,0.05)",
    boxShadow: "0 0 16px rgba(255,184,0,0.2)",
  },
  avatar: {
    width: "100%", height: "100%", borderRadius: "50%",
    background: `linear-gradient(135deg, ${G2}, ${GD})`,
    display: "flex", alignItems: "center", justifyContent: "center",
    fontFamily: "'Figtree',sans-serif",
    fontWeight: 900, fontSize: 22, color: "#0a0800",
  },
  greeting: {
    fontFamily: "'-apple-system','BlinkMacSystemFont','Figtree',sans-serif",
    fontSize: 12, color: "rgba(200,160,60,0.55)",
    letterSpacing: 0.5, marginBottom: 2,
  },
  userName: {
    fontFamily: "'Figtree',sans-serif",
    fontWeight: 700, fontSize: 22,
    background: `linear-gradient(135deg, #f0e0a0, ${G}, ${GD})`,
    WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
    letterSpacing: 2, marginBottom: 2,
  },
  userEmail: {
    fontFamily: "'-apple-system','BlinkMacSystemFont','Figtree',sans-serif",
    fontSize: 11, color: "rgba(200,160,60,0.4)",
  },
  headerRight: { display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6 },
  planBadge: {
    fontFamily: "'-apple-system','BlinkMacSystemFont','Figtree',sans-serif",
    fontWeight: 700, fontSize: 11,
    color: G, background: "rgba(255,184,0,0.08)",
    border: "1px solid rgba(255,184,0,0.22)",
    borderRadius: 20, padding: "6px 14px",
    letterSpacing: 1.5, display: "flex", alignItems: "center", gap: 6,
  },
  planDot: {
    width: 6, height: 6, borderRadius: "50%",
    background: G, boxShadow: `0 0 8px ${G}`,
    animation: "pulse-gold 2s ease-in-out infinite",
  },
  joinDate: {
    fontFamily: "'-apple-system','BlinkMacSystemFont','Figtree',sans-serif",
    fontSize: 11, color: "rgba(200,160,60,0.35)",
  },

  // Balance Hero
  balanceHero: {
    background: "rgba(10,8,3,0.97)",
    border: "1px solid rgba(255,184,0,0.18)",
    borderRadius: 16, padding: "28px 32px",
    marginBottom: 16,
    display: "flex", justifyContent: "space-between",
    alignItems: "center", flexWrap: "wrap", gap: 20,
    position: "relative", overflow: "hidden",
    boxShadow: "0 0 60px rgba(255,184,0,0.06), inset 0 0 60px rgba(255,184,0,0.02)",
  },
  bhGlow: {
    position: "absolute", top: 0, left: 0, right: 0, height: 2,
    background: `linear-gradient(90deg, transparent, ${G2}, ${G}, ${GD}, transparent)`,
  },
  bhLeft: {},
  bhLabel: {
    fontFamily: "'-apple-system','BlinkMacSystemFont','Figtree',sans-serif",
    fontSize: 12, color: "rgba(200,160,60,0.55)",
    letterSpacing: 1, textTransform: "uppercase", marginBottom: 6,
  },
  bhValue: {
    fontFamily: "'Figtree',sans-serif",
    fontWeight: 900, fontSize: "clamp(28px,4vw,44px)",
    background: `linear-gradient(135deg, ${G2}, ${G}, ${GD})`,
    WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
    letterSpacing: 1, marginBottom: 4,
  },
  bhSub: {
    fontFamily: "'-apple-system','BlinkMacSystemFont','Figtree',sans-serif",
    fontSize: 13, color: "rgba(200,160,60,0.4)",
  },
  bhRight: { display: "flex", gap: 12 },
  bhBtnPrimary: {
    fontFamily: "'-apple-system','BlinkMacSystemFont','Figtree',sans-serif",
    fontWeight: 700, fontSize: 14,
    color: "#0a0800",
    background: `linear-gradient(135deg, ${G2}, ${G}, ${GD})`,
    border: "none", borderRadius: 8,
    padding: "12px 28px",
    textDecoration: "none",
    boxShadow: "0 4px 20px rgba(255,184,0,0.35)",
    letterSpacing: 0.5,
  },
  bhBtnSecondary: {
    fontFamily: "'-apple-system','BlinkMacSystemFont','Figtree',sans-serif",
    fontWeight: 600, fontSize: 14,
    color: "#ff6b6b",
    background: "rgba(255,107,107,0.07)",
    border: "1px solid rgba(255,107,107,0.25)",
    borderRadius: 8, padding: "12px 28px",
    textDecoration: "none", letterSpacing: 0.5,
  },
  bhBgLogo: {
    position: "absolute", right: -20, top: "50%",
    transform: "translateY(-50%)",
    height: 140, opacity: 0.04,
    filter: "drop-shadow(0 0 20px rgba(255,184,0,0.5))",
    pointerEvents: "none",
  },

  // Stats
  statsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(210px,1fr))",
    gap: 12, marginBottom: 16,
  },
  statCard: {
    background: "rgba(10,8,3,0.95)",
    border: "1px solid rgba(255,184,0,0.1)",
    borderRadius: 12, padding: "18px 20px",
    position: "relative", overflow: "hidden",
  },
  scGlowLine: {
    position: "absolute", top: 0, left: 0, right: 0, height: 1,
    background: `linear-gradient(90deg, transparent, rgba(255,184,0,0.3), transparent)`,
  },
  scTop: {
    display: "flex", justifyContent: "space-between",
    alignItems: "center", marginBottom: 10,
  },
  scIcon: {
    width: 36, height: 36, borderRadius: 8,
    border: "1px solid", display: "flex",
    alignItems: "center", justifyContent: "center",
    fontSize: 15, fontWeight: 700,
  },
  scSub: {
    fontFamily: "'-apple-system','BlinkMacSystemFont','Figtree',sans-serif",
    fontSize: 10, color: "rgba(200,160,60,0.45)",
    letterSpacing: 0.5, textTransform: "uppercase",
  },
  scValue: {
    fontFamily: "'Figtree',sans-serif",
    fontWeight: 700, fontSize: 18,
    letterSpacing: 0.5, marginBottom: 3,
  },
  scLabel: {
    fontFamily: "'-apple-system','BlinkMacSystemFont','Figtree',sans-serif",
    fontSize: 12, color: "rgba(200,160,60,0.5)",
  },

  // Bottom
  bottomGrid: {
    display: "grid",
    gridTemplateColumns: "1.1fr 1fr",
    gap: 14, marginBottom: 16,
  },
  panel: {
    background: "rgba(10,8,3,0.95)",
    border: "1px solid rgba(255,184,0,0.1)",
    borderRadius: 12, padding: "22px",
  },
  panelHead: {
    display: "flex", alignItems: "center",
    gap: 10, marginBottom: 14,
  },
  panelIcon: { fontSize: 18, color: G },
  panelTitle: {
    fontFamily: "'Figtree',sans-serif",
    fontWeight: 600, fontSize: 12,
    color: "#f0e6c8", letterSpacing: 1, flex: 1,
  },
  panelBadge: {
    fontFamily: "'-apple-system','BlinkMacSystemFont','Figtree',sans-serif",
    fontWeight: 700, fontSize: 10,
    color: G, background: "rgba(255,184,0,0.1)",
    border: "1px solid rgba(255,184,0,0.25)",
    borderRadius: 4, padding: "3px 8px",
    letterSpacing: 0.5,
  },
  refDesc: {
    fontFamily: "'-apple-system','BlinkMacSystemFont','Figtree',sans-serif",
    fontSize: 12, color: "rgba(200,160,60,0.6)",
    lineHeight: 1.7, marginBottom: 14,
  },
  refBox: {
    display: "flex", alignItems: "center",
    background: "rgba(0,0,0,0.5)",
    border: "1px solid rgba(255,184,0,0.22)",
    borderRadius: 8, padding: "4px 4px 4px 14px",
    marginBottom: 16, gap: 8,
  },
  refCode: {
    fontFamily: "'Figtree',sans-serif",
    fontWeight: 700, fontSize: 16,
    color: G2, letterSpacing: 3, flex: 1,
  },
  copyBtn: {
    fontFamily: "'-apple-system','BlinkMacSystemFont','Figtree',sans-serif",
    fontWeight: 700, fontSize: 12,
    color: "#0a0800", border: "none",
    borderRadius: 6, padding: "8px 14px",
    cursor: "pointer", transition: "all 0.2s",
    letterSpacing: 0.5,
  },
  levelGrid: {
    display: "flex", gap: 6, flexWrap: "wrap",
  },
  levelItem: {
    background: "rgba(255,184,0,0.05)",
    border: "1px solid rgba(255,184,0,0.1)",
    borderRadius: 6, padding: "5px 8px",
    textAlign: "center", minWidth: 40,
  },
  levelNum: {
    fontFamily: "'-apple-system','BlinkMacSystemFont','Figtree',sans-serif",
    fontSize: 9, color: "rgba(200,160,60,0.4)",
    letterSpacing: 0.5,
  },
  levelPct: {
    fontFamily: "'Figtree',sans-serif",
    fontWeight: 700, fontSize: 11,
  },

  // AI Card
  aiCard: {
    background: "rgba(10,8,3,0.95)",
    border: "1px solid rgba(255,184,0,0.1)",
    borderRadius: 12, padding: "22px",
  },
  aiPrices: { display: "flex", flexDirection: "column", gap: 10 },
  aiRow: {
    display: "flex", alignItems: "center", gap: 10,
  },
  aiRowPair: {
    fontFamily: "'-apple-system','BlinkMacSystemFont','Figtree',sans-serif",
    fontSize: 12, color: "rgba(200,160,60,0.65)",
    width: 90, flexShrink: 0,
  },
  aiBar: {
    flex: 1, height: 6, background: "rgba(255,255,255,0.05)",
    borderRadius: 3, overflow: "hidden",
  },

  // Notices
  noticeList: { display: "flex", flexDirection: "column", gap: 8 },
  noticeItem: {
    borderLeft: "2px solid", paddingLeft: 10,
    paddingTop: 3, paddingBottom: 3,
  },
  noticeTitle: {
    fontFamily: "'-apple-system','BlinkMacSystemFont','Figtree',sans-serif",
    fontWeight: 600, fontSize: 12, color: "#f0e6c8", marginBottom: 2,
  },
  noticeMsg: {
    fontFamily: "'-apple-system','BlinkMacSystemFont','Figtree',sans-serif",
    fontSize: 11, color: "rgba(200,160,60,0.55)", lineHeight: 1.5,
  },
  noNotice: {
    fontFamily: "'-apple-system','BlinkMacSystemFont','Figtree',sans-serif",
    fontSize: 13, color: "rgba(200,160,60,0.3)",
    textAlign: "center", padding: "20px 0",
  },

  // Quick Actions
  quickActions: { marginTop: 6 },
  qaTitle: {
    fontFamily: "'-apple-system','BlinkMacSystemFont','Figtree',sans-serif",
    fontWeight: 700, fontSize: 11,
    color: "rgba(200,160,60,0.4)",
    letterSpacing: 1.5, textTransform: "uppercase",
    marginBottom: 10,
  },
  qaRow: { display: "flex", gap: 10, flexWrap: "wrap" },
  qaBtnGold: {
    fontFamily: "'-apple-system','BlinkMacSystemFont','Figtree',sans-serif",
    fontWeight: 700, fontSize: 13,
    color: "#0a0800",
    background: `linear-gradient(135deg, ${G2}, ${G}, ${GD})`,
    border: "none", borderRadius: 8,
    padding: "11px 22px",
    display: "flex", alignItems: "center", gap: 7,
    textDecoration: "none",
    boxShadow: "0 3px 16px rgba(255,184,0,0.3)",
    letterSpacing: 0.3,
  },
  qaBtnRed: {
    fontFamily: "'-apple-system','BlinkMacSystemFont','Figtree',sans-serif",
    fontWeight: 600, fontSize: 13,
    color: "#ff6b6b",
    background: "rgba(255,107,107,0.07)",
    border: "1px solid rgba(255,107,107,0.22)",
    borderRadius: 8, padding: "11px 22px",
    display: "flex", alignItems: "center", gap: 7,
    textDecoration: "none", letterSpacing: 0.3,
  },
  qaBtnGhost: {
    fontFamily: "'-apple-system','BlinkMacSystemFont','Figtree',sans-serif",
    fontWeight: 600, fontSize: 13,
    color: G,
    background: "rgba(255,184,0,0.06)",
    border: "1px solid rgba(255,184,0,0.2)",
    borderRadius: 8, padding: "11px 22px",
    display: "flex", alignItems: "center", gap: 7,
    textDecoration: "none", letterSpacing: 0.3,
    cursor: "pointer",
  },
};




